import { c as _c } from "react/compiler-runtime";
import clsx from "classnames";
import { NodeKey } from "lexical";
import { GridBlock, GridSlot, GridSlotSize } from "common/types/graphql";
import { FrontendBlock, ImageAspectRatio } from "modules/editor/types";
import { ContentSummaryVariant } from "common/components/ContentSummary";
import { BlockComponent } from "../BlocksRenderer";
import { ImmutableGridSlot } from "./ImmutableGridSlot";
import { MutableGridSlot } from "./MutableGridSlot";
import { imageSizes } from "common/lib/imageSizes";
import { usePageProps } from "common/hooks/data/usePageProps";
import type { EditPageProps } from "pages/edit/index.page";
import { ContentSummaryFrontendBlock } from "modules/editor/blocks/ContentSummary";
import type { DragItem } from "./types";
import { useCallback, useState } from "react";
export const INDEX_FOR_NO_GRID_SLOT = -1;
const slotSizeToContentSummaryPropsMap: Record<GridSlotSize, Partial<ContentSummaryFrontendBlock>> = {
  [GridSlotSize.Base]: {
    imageSizes: imageSizes({
      md: 0.25,
      sm: 0.5
    }),
    variant: ContentSummaryVariant.Base
  },
  [GridSlotSize.BaseWide]: {
    imageSizes: imageSizes({
      lg: 0.125,
      sm: 0.25
    }),
    variant: ContentSummaryVariant.BaseWide
  },
  [GridSlotSize.Lg]: {
    imageSizes: imageSizes({
      lg: 0.5,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.Xl]: {
    imageSizes: imageSizes({
      lg: 0.5,
      sm: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Landscape,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.Xxl]: {
    imageSizes: imageSizes(),
    desktopImageAspectRatio: ImageAspectRatio.Ultrawide,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.NearFullByFour]: {
    imageSizes: imageSizes({
      lg: 0.75,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.NearFullByThree]: {
    imageSizes: imageSizes({
      lg: 0.75,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    variant: ContentSummaryVariant.Hero,
    // Text only variants that pair with this don't need that much height
    className: "lg:min-h-fit xl:min-h-fit"
  },
  [GridSlotSize.NearFullByTwo]: {
    imageSizes: imageSizes({
      lg: 0.75,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.DisappearingThumbnail]: {
    imageSizes: imageSizes({
      lg: 0.2,
      sm: 0.25
    }),
    variant: ContentSummaryVariant.DisappearingThumbnail
  }
};

/**
 * Configuration for blocks based on the slot size.
 *
 * This function adds additional props to the blocks based on the slot size the slot size they are positioned.
 * These additional props can define what variant, image aspect ratio and image sizes the block should use.
 *
 * The extra props will only be passed to the block if the block is a ContentSummary or CustomURL block, otherwise
 * the block will be returned as is.
 */
const getAdjustedBlocksConfig = (blocks: FrontendBlock[], slotSizes: GridSlotSize[]): FrontendBlock[] => slotSizes.map((slotSize, index: number) => {
  const block = blocks[index] || null;
  if (block?.type === "CONTENT_SUMMARY" || block?.type === "CUSTOM_URL") {
    return {
      ...block,
      ...slotSizeToContentSummaryPropsMap[slotSize]
    };
  }
  return block;
});
export interface GridProps extends Omit<GridBlock, "blocks" | "columns"> {
  blocks: FrontendBlock[];
  slotSizes: GridSlotSize[];
  slotTypes: GridSlot[];
  nodeKey?: NodeKey;
  onGridSlotClear?: (index: number) => void;
  onGridSlotDrop?: (index: number, item: DragItem) => void;
  onGridSlotSwap?: (a: number, b: number, item: DragItem) => void;
  onGridSlotEdit?: (index: number) => void;
  onGridSlotAdd?: (index: number) => void;
}
export const Grid = t0 => {
  "use memo";

  const $ = _c(10);
  const {
    blocks: t1,
    slotSizes: t2,
    slotTypes: t3,
    nodeKey,
    onGridSlotClear,
    onGridSlotDrop,
    onGridSlotSwap,
    onGridSlotEdit,
    onGridSlotAdd
  } = t0;
  const blocks = t1 === undefined ? [] : t1;
  const slotSizes = t2 === undefined ? [] : t2;
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const slotTypes = t4;
  const {
    editing
  } = usePageProps();
  const [dragOver, setDragOver] = useState(INDEX_FOR_NO_GRID_SLOT);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = index => {
      setDragOver(index);
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const handleDraggingOver = t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setDragOver(INDEX_FOR_NO_GRID_SLOT);
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const handleDragLeave = t6;
  const t7 = editing || blocks?.length > 0;
  let t8;
  if ($[4] !== t7) {
    t8 = clsx("grid grid-cols-3 gap-4 sm:grid-cols-12 sm:gap-6 sm:gap-y-2 xl:gap-x-8 xl:gap-y-4", {
      "mb-4": t7
    });
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const t9 = "grid";
  const t10 = getAdjustedBlocksConfig(blocks, slotSizes).map((block, index_0) => {
    const slotType = slotTypes?.[index_0] || GridSlot.Editorial;
    const slotSize = slotSizes[index_0];
    if (editing) {
      return block === null ? <MutableGridSlot key={index_0} gridNodeKey={nodeKey} index={index_0} size={slotSize} type={slotType} onDrop={onGridSlotDrop} onSwap={onGridSlotSwap} onAdd={onGridSlotAdd} onDraggingOver={handleDraggingOver} dragOver={dragOver === index_0} asPlaceholder={true} /> : <MutableGridSlot key={index_0} gridNodeKey={nodeKey} index={index_0} size={slotSize} type={slotType} onClear={onGridSlotClear} onDrop={onGridSlotDrop} onSwap={onGridSlotSwap} onEdit={onGridSlotEdit} onDraggingOver={handleDraggingOver} dragOver={dragOver === index_0}><BlockComponent block={block} /></MutableGridSlot>;
    }
    return block === null ? null : <ImmutableGridSlot key={index_0} size={slotSize} type={slotType}><BlockComponent block={block} /></ImmutableGridSlot>;
  });
  let t11;
  if ($[6] !== handleDragLeave || $[7] !== t10 || $[8] !== t8) {
    t11 = <section className={t8} onDragLeave={handleDragLeave} data-testid={t9}>{t10}</section>;
    $[6] = handleDragLeave;
    $[7] = t10;
    $[8] = t8;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  return t11;
};